import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, BookOpen, Menu, AlertCircle, RefreshCw, BookmarkPlus, Bookmark, Trash2, StickyNote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Define the props interface for the EbookViewer component
interface EbookViewerProps {
  src: string; // URL of the ebook file (PDF or EPUB)
  title: string; // Title of the ebook
  type: 'pdf' | 'epub'; // Type of the ebook file
}

type BookmarkItem = {
  id?: string;
  page: number;
  title: string;
  href?: string;
  cfi?: string;
  note?: string;
  createdAt?: string;
  isCustom?: boolean;
};

const EbookViewer: React.FC<EbookViewerProps> = ({ src, title, type }) => {
  const { select, locale } = useLanguage();
  // State variables for viewer functionality
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>([]);
  const [userBookmarks, setUserBookmarks] = useState<BookmarkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [navigating, setNavigating] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<Record<string, unknown> | null>(null);

  // Refs for DOM elements and instances
  const viewerRef = useRef<HTMLDivElement>(null);
  const bookRef = useRef<Record<string, unknown> | null>(null);
  const renditionRef = useRef<Record<string, unknown> | null>(null);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const userBookmarksKey = React.useMemo(() => `ebook-bookmarks::${encodeURIComponent(src)}`, [src]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(userBookmarksKey);
      if (!stored) {
        setUserBookmarks([]);
        return;
      }

      const parsed = JSON.parse(stored);
      const normalize = (list: BookmarkItem[]) =>
        list.map((bookmark, index) => ({
          ...bookmark,
          id: bookmark.id || `${index}-${bookmark.page}-${bookmark.href || bookmark.cfi || 'bookmark'}`,
          isCustom: true,
        }));

      if (Array.isArray(parsed)) {
        setUserBookmarks(normalize(parsed as BookmarkItem[]));
      } else if (parsed && Array.isArray(parsed.bookmarks)) {
        setUserBookmarks(normalize(parsed.bookmarks as BookmarkItem[]));
      } else {
        setUserBookmarks([]);
      }
    } catch (err) {
      console.error('Error restoring ebook bookmarks:', err);
      setUserBookmarks([]);
    }
  }, [userBookmarksKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(userBookmarksKey, JSON.stringify(userBookmarks));
    } catch (err) {
      console.error('Error saving ebook bookmarks:', err);
    }
  }, [userBookmarks, userBookmarksKey]);

  const pageLabel = select({ es: 'Página', en: 'Page' });
  const addBookmarkActionLabel = select({ es: 'Guardar marcador', en: 'Save bookmark' });
  const savedBookmarksLabel = select({ es: 'Marcadores guardados', en: 'Saved bookmarks' });
  const noSavedBookmarksLabel = select({ es: 'Aún no has guardado marcadores.', en: 'You have not saved any bookmarks yet.' });
  const defaultBookmarksLabel = select({ es: 'Tabla de contenidos', en: 'Table of contents' });
  const removeBookmarkLabel = select({ es: 'Eliminar marcador', en: 'Remove bookmark' });
  const addBookmarkNotePrompt = select({ es: 'Añade una nota opcional para este marcador', en: 'Add an optional note for this bookmark' });
  const savedOnLabel = select({ es: 'Guardado', en: 'Saved' });
  const bookmarkBaseTitle = select({ es: 'Marcador', en: 'Bookmark' });
  const noDefaultBookmarksLabel = select({ es: 'Este libro no incluye tabla de contenidos.', en: 'This book does not include a table of contents.' });
  const bookmarksNavigationLabel = select({ es: 'Marcadores y navegación', en: 'Bookmarks & navigation' });

  const formatSavedAt = (value?: string) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString(locale, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch (err) {
      console.error('Error formatting saved date:', err);
      return value;
    }
  };

  const handleAddBookmark = () => {
    if (loading) return;

    const noteInput = window.prompt(addBookmarkNotePrompt, '');
    const note = noteInput && noteInput.trim().length > 0 ? noteInput.trim() : undefined;

    const location = type === 'epub' ? renditionRef.current?.currentLocation() : null;
    const epubCfi = location?.start?.cfi;
    const epubHref = location?.start?.href || location?.start?.index?.href;
    const pageNumber = type === 'epub' ? (location?.start?.displayed?.page || currentPage) : currentPage;

    if (type === 'pdf' && !pdfDoc) {
      return;
    }

    const newBookmark: BookmarkItem = {
      id: `${Date.now()}`,
      page: pageNumber,
      title: `${bookmarkBaseTitle} • ${pageLabel} ${pageNumber}`,
      note,
      cfi: type === 'epub' ? epubCfi : undefined,
      href: type === 'epub' ? epubHref : undefined,
      createdAt: new Date().toISOString(),
      isCustom: true,
    };

    setUserBookmarks(prev => {
      const filtered = prev.filter((bookmark) => {
        if (!bookmark.isCustom) return true;
        if (type === 'pdf') {
          return bookmark.page !== pageNumber;
        }
        if (newBookmark.cfi && bookmark.cfi) {
          return bookmark.cfi !== newBookmark.cfi;
        }
        return bookmark.page !== pageNumber;
      });
      return [newBookmark, ...filtered];
    });

    setShowBookmarks(true);
  };

  const handleRemoveBookmark = (bookmarkId: string | undefined) => {
    if (!bookmarkId) return;
    setUserBookmarks(prev => prev.filter(bookmark => bookmark.id !== bookmarkId));
  };

  // Function to clean up instances
  const cleanupEpub = useCallback(() => {
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
    
    try {
      if (renditionRef.current) {
        renditionRef.current.destroy();
        renditionRef.current = null;
      }
      if (bookRef.current) {
        bookRef.current.destroy();
        bookRef.current = null;
      }
    } catch (err) {
      console.error('Error during cleanup:', err);
    }
  }, []);

  // Function to render a specific PDF page
  const renderPdfPage = async (pageNum: number, docOverride?: Record<string, unknown>) => {
    const doc = docOverride ?? pdfDoc;
    if (!doc || !canvasRef.current) {
      console.warn('renderPdfPage skipped: missing document or canvas reference');
      return;
    }

    try {
      console.log('Rendering PDF page:', pageNum);
      const page = await doc.getPage(pageNum);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (!context) {
        console.error('Could not get canvas context');
        return;
      }

      // Get the page viewport at a fixed scale for consistent rendering
      const viewport = page.getViewport({ scale: 1.5 }); // Fixed scale for good quality
      
      // Set canvas size
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Apply zoom using CSS transform instead of re-rendering
      canvas.style.transform = `scale(${zoom})`;
      canvas.style.transformOrigin = 'top center';

      // Clear previous content
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Set high quality rendering
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      
      // Render page
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        intent: 'display'
      };
      
      await page.render(renderContext).promise;

      // Add watermark overlay
      addPdfWatermark(context, canvas.width, canvas.height);

      setCurrentPage(pageNum);
    } catch (err) {
      console.error('Error rendering PDF page:', err);
      setError(
        select({
          es: `Error al mostrar la página ${pageNum} del PDF: ${err.message}`,
          en: `Failed to display PDF page ${pageNum}: ${err.message}`,
        })
      );
    }
  };

  // Function to load PDF using PDF.js (alternative to iframe)
  const loadPdfWithPdfJs = useCallback(async () => {
    console.log('Loading PDF with PDF.js from:', src);
    setLoading(true);
    setError('');

    try {
      // Load PDF.js from CDN with proper global configuration
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      
      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      
      // Configure PDF.js worker
      const winPdfjsLib = (window as Record<string, unknown>).pdfjsLib as Record<string, unknown> | undefined;
      if (winPdfjsLib) {
        const globalOpts = winPdfjsLib.GlobalWorkerOptions as Record<string, unknown> | undefined;
        if (globalOpts) {
          globalOpts.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }
      }

      // Load PDF from Supabase URL
      const pdfjsLib = (window as Record<string, unknown>).pdfjsLib as Record<string, unknown>;
      const getDocumentFn = pdfjsLib.getDocument as (options: Record<string, unknown>) => { promise: Promise<Record<string, unknown>> };
      const loadingTask = getDocumentFn({
        url: src,
        httpHeaders: {
          'Origin': window.location.origin,
        },
        withCredentials: false,
        disableRange: false,
        disableStream: false,
        disableAutoFetch: false,
        disableFontFace: false
      });

      const pdf = await Promise.race([
        loadingTask.promise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('PDF loading timeout')), 30000)
        )
      ]);

      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);

      // Create bookmarks from PDF outline if available
      try {
        const outline = await pdf.getOutline();
        if (outline && outline.length > 0) {
          const pdfBookmarks = outline.slice(0, 10).map((item: Record<string, unknown>, index: number) => ({
            page: item.dest ? (Array.isArray(item.dest) ? item.dest[0] : index + 1) : index + 1,
            title: item.title || select({
              es: `Capítulo ${index + 1}`,
              en: `Chapter ${index + 1}`,
            }),
            isCustom: false,
          }));
          setBookmarks(pdfBookmarks);
        } else {
          // Generate default bookmarks
          const defaultBookmarks = Array.from({ length: Math.min(5, pdf.numPages) }, (_, i) => ({
            page: Math.floor((pdf.numPages / 5) * i) + 1,
            title: select({ es: `Sección ${i + 1}`, en: `Section ${i + 1}` }),
            isCustom: false,
          }));
          setBookmarks(defaultBookmarks);
        }
      } catch (outlineError) {
        console.warn('Could not load PDF outline:', outlineError);
        // Fallback bookmarks
        setBookmarks([
          { page: 1, title: select({ es: 'Inicio', en: 'Beginning' }), isCustom: false },
          { page: Math.floor(pdf.numPages / 2), title: select({ es: 'Mitad', en: 'Middle' }), isCustom: false },
          { page: pdf.numPages, title: select({ es: 'Final', en: 'End' }), isCustom: false }
        ]);
      }

      // Render first page
      await renderPdfPage(1, pdf);
      
      console.log('PDF loaded successfully with', pdf.numPages, 'pages');
    } catch (err) {
      console.error('Error loading PDF:', err);
      if (err instanceof Error) {
        if (err.message.includes('timeout') || err.message.includes('Timeout')) {
          setError(
            select({
              es: 'El PDF está tardando demasiado en cargar. Verifica tu conexión a internet.',
              en: 'The PDF is taking too long to load. Please check your internet connection.',
            })
          );
        } else if (err.message.includes('CORS') || err.message.includes('cross-origin')) {
          setError(
            select({
              es: 'Error de acceso al archivo PDF. Problema de configuración del servidor.',
              en: 'Unable to access the PDF file. Server configuration issue detected.',
            })
          );
        } else if (err.message.includes('Invalid PDF')) {
          setError(
            select({
              es: 'El archivo PDF está corrupto o no es válido.',
              en: 'The PDF file is corrupted or invalid.',
            })
          );
        } else {
          setError(
            select({
              es: `Error cargando PDF: ${err.message}`,
              en: `Error loading PDF: ${err.message}`,
            })
          );
        }
      } else {
        setError(
          select({
            es: 'Error desconocido cargando el PDF.',
            en: 'Unknown error while loading the PDF.',
          })
        );
      }
    } finally {
      setLoading(false);
    }
  }, [src]);

  // Add security watermark to PDF
  const addPdfWatermark = (context: CanvasRenderingContext2D, width: number, height: number) => {
    context.save();
    context.globalAlpha = 0.15;
    context.font = '24px Arial';
    context.fillStyle = '#9333EA';
    context.textAlign = 'center';
    
    // Add diagonal watermarks
    for (let i = 0; i < width; i += 200) {
      for (let j = 0; j < height; j += 150) {
        context.save();
        context.translate(i + 100, j + 75);
        context.rotate(-Math.PI / 6);
        context.fillText(select({ es: 'SoArtistic © Protegido', en: 'SoArtistic © Protected' }), 0, 0);
        context.restore();
      }
    }
    
    context.restore();
  };

  // Function to load and render an EPUB file
  const loadEpub = useCallback(async (initialZoom: number = 1) => {
    console.log('Attempting to load EPUB from:', src);
    setLoading(true);
    setError('');
    cleanupEpub();

    try {
      // Dynamically import epubjs
      const epubModule = await import('epubjs');
      const ePubModule = epubModule as Record<string, unknown>;
      const ePub = (ePubModule.default as unknown) || epubModule;

      if (!ePub) {
        throw new Error('Could not load EPUB library');
      }

      // Create EPUB book with better error handling
      const book = await Promise.race([
        new ePub(src, {
          openAs: 'epub',
          encoding: 'utf8',
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('EPUB loading timeout')), 20000)
        )
      ]);

      bookRef.current = book;
      console.log('EPUB loaded successfully');

      if (viewerRef.current) {
        // Create rendition with security settings
        const rendition = book.renderTo(viewerRef.current, {
          width: '100%',
          height: '100%',
          spread: 'none',
          flow: 'paginated',
          manager: 'default',
          allowScriptedContent: false,
          snap: true,
          stylesheet: `
            body { 
              user-select: none !important; 
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              pointer-events: auto !important;
              font-size: ${16 * initialZoom}px !important;
            }
            * { 
              user-select: none !important; 
              -webkit-touch-callout: none !important;
            }
          `
        });

        renditionRef.current = rendition;

        // Apply theme with watermark
        rendition.themes.register('protected', {
          'body': {
            'font-family': 'Georgia, serif !important',
            'font-size': `${16 * initialZoom}px !important`,
            'line-height': '1.6 !important',
            'padding': '20px !important',
            'margin': '0 !important',
            'color': '#333 !important',
            'background-color': '#fff !important',
            'user-select': 'none !important',
            '-webkit-user-select': 'none !important',
            'position': 'relative !important'
          },
          'body::before': {
            'content': `"${select({ es: 'Contenido protegido SoArtistic', en: 'SoArtistic Protected Content' })}"`,
            'position': 'fixed',
            'top': '50%',
            'left': '50%',
            'transform': 'translate(-50%, -50%) rotate(-45deg)',
            'font-size': '48px',
            'color': 'rgba(147, 51, 234, 0.1)',
            'pointer-events': 'none',
            'z-index': '9999',
            'font-weight': 'bold'
          },
          'p': {
            'margin-bottom': '1em !important',
            'text-align': 'justify !important'
          }
        });
        
        rendition.themes.select('protected');

        // Display first page
        await rendition.display();
        
        // Auto-navigate to first page to ensure content is visible
        setTimeout(() => {
          if (renditionRef.current) {
            try {
              renditionRef.current.display(0); // Display first chapter/page
            } catch (err) {
              console.warn('Auto-navigation to first page failed:', err);
            }
          }
        }, 100);
        
        console.log('EPUB rendered successfully');

        // Load table of contents
        try {
          const navigation = await book.loaded.navigation;
          if (navigation?.toc?.length > 0) {
            const toc = navigation.toc.slice(0, 15).map((item: Record<string, unknown>, index: number) => ({
              page: index + 1,
              title: item.label?.trim() || select({ es: `Capítulo ${index + 1}`, en: `Chapter ${index + 1}` }),
              href: item.href,
              isCustom: false,
            }));
            setBookmarks(toc);
          } else {
            // Fallback bookmarks
            const defaultBookmarks = book.spine.spineItems.slice(0, 10).map((item: Record<string, unknown>, index: number) => ({
              page: index + 1,
              title: select({ es: `Capítulo ${index + 1}`, en: `Chapter ${index + 1}` }),
              href: item.href,
              isCustom: false,
            }));
            setBookmarks(defaultBookmarks);
          }
        } catch (navError) {
          console.warn('Error loading navigation:', navError);
          setBookmarks([
            { page: 1, title: select({ es: 'Inicio', en: 'Beginning' }), isCustom: false },
            { page: 2, title: select({ es: 'Contenido', en: 'Content' }), isCustom: false }
          ]);
        }

        // Handle page changes
        rendition.on('relocated', (location: Record<string, unknown>) => {
          try {
            if (location?.start?.displayed) {
              setCurrentPage(location.start.displayed.page || 1);
              setTotalPages(location.start.displayed.total || 1);
            }
          } catch (err) {
            console.warn('Error handling relocated event:', err);
          }
        });

        // Disable context menu on rendition
        rendition.on('rendered', () => {
          const iframe = viewerRef.current?.querySelector('iframe');
          if (iframe?.contentDocument) {
            iframe.contentDocument.addEventListener('contextmenu', (e) => e.preventDefault());
            iframe.contentDocument.addEventListener('selectstart', (e) => e.preventDefault());
          }
        });

      } else {
        setError(
          select({
            es: 'Error: contenedor del visor no disponible',
            en: 'Error: viewer container not available',
          })
        );
      }
    } catch (err) {
      console.error('Error loading EPUB:', err);
      if (err instanceof Error) {
        if (err.message.includes('Timeout') || err.message.includes('timeout')) {
          setError(
            select({
              es: 'El libro está tardando demasiado en cargar. Verifica tu conexión.',
              en: 'The book is taking too long to load. Please check your connection.',
            })
          );
        } else if (err.message.includes('CORS') || err.message.includes('cross-origin')) {
          setError(
            select({
              es: 'Error de acceso al archivo. El servidor de Supabase puede estar bloqueando la conexión.',
              en: 'Unable to access the file. Supabase may be blocking the connection.',
            })
          );
        } else if (err.message.includes('fetch')) {
          setError(
            select({
              es: 'No se puede acceder al archivo EPUB desde Supabase. Problema de red o configuración.',
              en: 'Cannot access the EPUB file from Supabase. Network or configuration issue.',
            })
          );
        } else {
          setError(
            select({
              es: `Error cargando EPUB: ${err.message}`,
              en: `Error loading EPUB: ${err.message}`,
            })
          );
        }
      } else {
        setError(
          select({
            es: 'Error desconocido cargando el libro.',
            en: 'Unknown error while loading the book.',
          })
        );
      }
    } finally {
      setLoading(false);
    }
  }, [src, cleanupEpub]);

  // Navigation handlers with debouncing
  const debouncedNavigate = useCallback((direction: 'prev' | 'next') => {
    if (navigating) return;

    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    setNavigating(true);

    try {
      if (type === 'epub' && renditionRef.current) {
        if (direction === 'prev') {
          renditionRef.current.prev();
        } else {
          renditionRef.current.next();
        }
      } else if (type === 'pdf' && pdfDoc) {
        const newPage = direction === 'prev' 
          ? Math.max(1, currentPage - 1)
          : Math.min(totalPages, currentPage + 1);
        
        if (newPage !== currentPage) {
          renderPdfPage(newPage);
        }
      }
    } catch (err) {
      console.error('Error during navigation:', err);
    }

    navigationTimeoutRef.current = setTimeout(() => {
      setNavigating(false);
      navigationTimeoutRef.current = null;
    }, 300);
  }, [navigating, type, currentPage, totalPages, pdfDoc]);

  // Main useEffect to load content
  useEffect(() => {
    if (type === 'epub') {
      loadEpub(zoom);
    } else if (type === 'pdf') {
      loadPdfWithPdfJs();
    }

    return () => {
      cleanupEpub();
    };
  }, [src, type]);

  // Separate useEffect for PDF zoom changes
  useEffect(() => {
    if (type === 'pdf' && canvasRef.current && pdfDoc) {
      // Apply zoom transform without re-rendering
      const canvas = canvasRef.current;
      canvas.style.transform = `scale(${zoom})`;
      canvas.style.transformOrigin = 'center center';
    }
  }, [zoom, type, pdfDoc]);

  // Security: Prevent right-click and selection
  const preventContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const preventTextSelection = (e: React.MouseEvent) => {
    e.preventDefault();
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
  };

  // Security: Disable keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable copy/save shortcuts
      if (
        (e.ctrlKey || e.metaKey) && 
        (e.key === 'c' || e.key === 's' || e.key === 'a' || e.key === 'p' || 
         e.key === 'C' || e.key === 'S' || e.key === 'A' || e.key === 'P')
      ) {
        e.preventDefault();
        return false;
      }
      
      // Disable developer tools
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navigation handlers
  const handlePrevPage = () => debouncedNavigate('prev');
  const handleNextPage = () => debouncedNavigate('next');

  const handleReload = () => {
    setNavigating(false);
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
    
    if (type === 'epub') {
      loadEpub();
    } else {
      loadPdfWithPdfJs();
    }
  };

  // Zoom handlers for PDF
  const handleZoomIn = () => {
    if (type === 'pdf' && pdfDoc) {
      const newZoom = Math.min(3, zoom + 0.25);
      setZoom(newZoom);
      // Don't re-render, just update zoom state for CSS transform
    } else if (type === 'epub') {
      const newZoom = Math.min(3, zoom + 0.25);
      setZoom(newZoom);
      // Apply zoom to existing rendition instead of reloading
      if (renditionRef.current) {
        const iframe = viewerRef.current?.querySelector('iframe');
        if (iframe?.contentDocument) {
          iframe.style.transform = `scale(${newZoom})`;
          iframe.style.transformOrigin = 'top center';
        }
      }
    }
  };

  const handleZoomOut = () => {
    if (type === 'pdf' && pdfDoc) {
      const newZoom = Math.max(0.5, zoom - 0.25);
      setZoom(newZoom);
      // Don't re-render, just update zoom state for CSS transform
    } else if (type === 'epub') {
      const newZoom = Math.max(0.5, zoom - 0.25);
      setZoom(newZoom);
      // Apply zoom to existing rendition instead of reloading
      if (renditionRef.current) {
        const iframe = viewerRef.current?.querySelector('iframe');
        if (iframe?.contentDocument) {
          iframe.style.transform = `scale(${newZoom})`;
          iframe.style.transformOrigin = 'top center';
        }
      }
    }
  };

  const handleBookmarkClick = (bookmark: BookmarkItem) => {
    if (navigating) return;
    
    if (type === 'epub' && renditionRef.current) {
      setNavigating(true);
      try {
        const target = bookmark.cfi || bookmark.href;
        if (target) {
          renditionRef.current.display(target);
        }
        setShowBookmarks(false);
      } catch (err) {
        console.error('Error navigating to bookmark:', err);
      } finally {
        setTimeout(() => setNavigating(false), 300);
      }
    } else if (type === 'pdf' && pdfDoc) {
      if (bookmark.page) {
        renderPdfPage(bookmark.page);
        setShowBookmarks(false);
      }
    } else if (type === 'pdf' && !pdfDoc) {
      console.warn('Bookmark navigation skipped: PDF document not ready');
    }
  };

  // Error display component
  if (error) {
    return (
      <div className="w-full h-full flex flex-col bg-gray-100 rounded-lg">
        <div className="flex items-center justify-between p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-red-600" size={20} />
            <span className="font-medium text-red-800">
              {select({ es: 'Error cargando contenido', en: 'Error loading content' })}
            </span>
          </div>
          <button
            onClick={handleReload}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            title={select({ es: 'Reintentar', en: 'Retry' })}
          >
            <RefreshCw size={16} className="text-red-600" />
          </button>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {select({ es: 'No se puede mostrar el contenido', en: 'Content cannot be displayed' })}
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">{error}</p>
            
            <div className="space-y-3">
              <button
                onClick={handleReload}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} />
                {select({ es: 'Reintentar carga', en: 'Retry loading' })}
              </button>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h4 className="font-medium text-blue-900 mb-2">
                  {select({ es: 'Posibles soluciones:', en: 'Possible solutions:' })}
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>{select({ es: '• Verifica tu conexión a internet', en: '• Check your internet connection' })}</li>
                  <li>{select({ es: '• El archivo puede estar temporalmente no disponible', en: '• The file may be temporarily unavailable' })}</li>
                  <li>{select({ es: '• Contacta con soporte si el problema persiste', en: '• Contact support if the issue persists' })}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main render for EPUB
  if (type === 'epub') {
    return (
      <div 
        className="w-full h-full flex flex-col bg-gray-50 rounded-lg shadow-lg overflow-hidden select-none"
        onContextMenu={preventContextMenu}
        onMouseDown={preventTextSelection}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3">
            <BookOpen size={20} className="text-purple-600" />
            <span className="font-medium truncate text-gray-900">{title}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleAddBookmark}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              disabled={loading}
              title={addBookmarkActionLabel}
            >
              <BookmarkPlus size={16} />
            </button>
            <button
              onClick={() => setShowBookmarks(!showBookmarks)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              disabled={loading}
              title={select({ es: 'Tabla de contenidos', en: 'Table of contents' })}
            >
              <Menu size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Bookmarks Sidebar */}
          {showBookmarks && (
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <Bookmark size={16} className="text-purple-600" />
                  <span>{select({ es: 'Marcadores y contenido', en: 'Bookmarks & contents' })}</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-5">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Bookmark size={12} />
                    {savedBookmarksLabel}
                  </h4>
                  {userBookmarks.length === 0 ? (
                    <p className="text-xs text-gray-500">{noSavedBookmarksLabel}</p>
                  ) : (
                    <div className="space-y-2">
                      {userBookmarks.map((bookmark) => (
                        <div key={bookmark.id} className="group relative">
                          <button
                            onClick={() => handleBookmarkClick(bookmark)}
                            className="w-full text-left p-3 rounded-md border border-purple-100 bg-purple-50 hover:bg-purple-100 transition-colors"
                          >
                            <div className="text-sm font-semibold text-purple-800 line-clamp-2">
                              {bookmark.title}
                            </div>
                            <div className="text-xs text-purple-600 mt-1">
                              {pageLabel} {bookmark.page}
                            </div>
                            {bookmark.note && (
                              <div className="text-xs text-purple-700 mt-2 flex items-center gap-1">
                                <StickyNote size={12} />
                                <span className="line-clamp-2">{bookmark.note}</span>
                              </div>
                            )}
                            {bookmark.createdAt && (
                              <div className="text-[10px] text-purple-500 mt-1">{savedOnLabel}: {formatSavedAt(bookmark.createdAt)}</div>
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveBookmark(bookmark.id);
                            }}
                            className="absolute top-2 right-2 p-1 text-purple-400 hover:text-red-500 transition-colors"
                            title={removeBookmarkLabel}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Menu size={12} />
                    {defaultBookmarksLabel}
                  </h4>
                  {bookmarks.length === 0 ? (
                    <p className="text-xs text-gray-500">{noDefaultBookmarksLabel}</p>
                  ) : (
                    <div className="space-y-1">
                      {bookmarks.map((bookmark, index) => (
                        <button
                          key={bookmark.href || bookmark.cfi || `default-${index}`}
                          onClick={() => handleBookmarkClick(bookmark)}
                          className="w-full text-left p-3 rounded-md hover:bg-gray-100 transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900 line-clamp-2">
                            {bookmark.title}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {pageLabel} {bookmark.page}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* EPUB Content */}
          <div className="flex-1 flex flex-col min-w-0">
            <div
              ref={viewerRef}
              className="flex-1 overflow-hidden bg-white relative"
              style={{ userSelect: 'none' }}
              onContextMenu={preventContextMenu}
              onSelectStart={(e) => e.preventDefault()}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">{select({ es: 'Cargando libro...', en: 'Loading book...' })}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between p-4 bg-white border-t border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              disabled={loading || zoom <= 0.5}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-sm min-w-16 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={handleZoomIn}
              disabled={loading || zoom >= 3}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <ZoomIn size={16} />
            </button>
          </div>

          <div className="flex items-center gap-4 bg-gray-50 px-4 py-2 rounded-lg">
            <button
              onClick={handlePrevPage}
              disabled={loading || navigating}
              className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors disabled:opacity-50"
            >
              <ChevronLeft size={18} />
            </button>

            <span className="text-sm text-gray-700 font-medium min-w-20 text-center">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={handleNextPage}
              disabled={loading || navigating}
              className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors disabled:opacity-50"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500 bg-purple-50 px-2 py-1 rounded">
              {select({ es: '© SoArtistic Protegido', en: '© SoArtistic Protected' })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main render for PDF with Canvas
  return (
    <div 
      className="w-full h-full flex flex-col bg-gray-900 rounded-lg shadow-lg overflow-hidden select-none"
      onContextMenu={preventContextMenu}
      onMouseDown={preventTextSelection}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {/* PDF Toolbar */}
      <div className="flex items-center justify-between p-4 bg-gray-800 text-white">
        <div className="flex items-center gap-3">
          <BookOpen size={20} className="text-blue-400" />
          <span className="font-medium truncate">{title}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleAddBookmark}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            disabled={loading}
            title={addBookmarkActionLabel}
          >
            <BookmarkPlus size={16} />
          </button>
          <button
            onClick={() => setShowBookmarks(!showBookmarks)}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            title={select({ es: 'Índice', en: 'Table of contents' })}
          >
            <Menu size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* PDF Bookmarks */}
        {showBookmarks && (
          <div className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center gap-2 text-white font-semibold">
                <Bookmark size={16} className="text-blue-400" />
                <span>{bookmarksNavigationLabel}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-5">
              <div>
                <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Bookmark size={12} className="text-blue-300" />
                  {savedBookmarksLabel}
                </h4>
                {userBookmarks.length === 0 ? (
                  <p className="text-xs text-gray-400">{noSavedBookmarksLabel}</p>
                ) : (
                  <div className="space-y-2">
                    {userBookmarks.map((bookmark) => (
                      <div key={bookmark.id} className="group relative">
                        <button
                          onClick={() => handleBookmarkClick(bookmark)}
                          className="w-full text-left p-3 rounded-md border border-blue-500/30 bg-gray-700 hover:bg-gray-600 transition-colors text-blue-100"
                        >
                          <div className="text-sm font-semibold text-blue-100 line-clamp-2">
                            {bookmark.title}
                          </div>
                          <div className="text-xs text-blue-300 mt-1">
                            {pageLabel} {bookmark.page}
                          </div>
                          {bookmark.note && (
                            <div className="text-xs text-blue-200 mt-2 flex items-center gap-1">
                              <StickyNote size={12} />
                              <span className="line-clamp-2">{bookmark.note}</span>
                            </div>
                          )}
                          {bookmark.createdAt && (
                            <div className="text-[10px] text-blue-300 mt-1">{savedOnLabel}: {formatSavedAt(bookmark.createdAt)}</div>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveBookmark(bookmark.id);
                          }}
                          className="absolute top-2 right-2 p-1 text-blue-300 hover:text-red-400 transition-colors"
                          title={removeBookmarkLabel}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Menu size={12} className="text-gray-400" />
                  {defaultBookmarksLabel}
                </h4>
                {bookmarks.length === 0 ? (
                  <p className="text-xs text-gray-400">{noDefaultBookmarksLabel}</p>
                ) : (
                  <div className="space-y-1">
                    {bookmarks.map((bookmark, index) => (
                      <button
                        key={bookmark.href || bookmark.cfi || `default-${index}`}
                        onClick={() => handleBookmarkClick(bookmark)}
                        className="w-full text-left p-3 rounded-md hover:bg-gray-700 transition-colors text-white"
                      >
                        <div className="text-sm font-medium line-clamp-2">
                          {bookmark.title}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {pageLabel} {bookmark.page}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PDF Canvas Content */}
        <div className="flex-1 flex flex-col min-w-0 bg-gray-800">
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto" style={{ minHeight: 0 }}>
            {loading ? (
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
                <p>{select({ es: 'Cargando PDF...', en: 'Loading PDF...' })}</p>
              </div>
            ) : (
              <div className="relative flex items-center justify-center w-full h-full" style={{ minHeight: 0, minWidth: 0 }}>
                <canvas
                  ref={canvasRef}
                  className="shadow-2xl rounded-lg"
                  style={{ 
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    pointerEvents: 'auto',
                    maxWidth: zoom > 1 ? 'none' : '100%',
                    maxHeight: zoom > 1 ? 'none' : '100%',
                    transition: 'transform 0.2s ease-out'
                  }}
                  onContextMenu={preventContextMenu}
                  onSelectStart={(e) => e.preventDefault()}
                />
                
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Navigation */}
      <div className="flex items-center justify-between p-4 bg-gray-800 text-white">
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            disabled={loading || zoom <= 0.5}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <ZoomOut size={16} />
          </button>
          <span className="text-sm min-w-16 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={handleZoomIn}
            disabled={loading || zoom >= 3}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <ZoomIn size={16} />
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handlePrevPage}
            disabled={loading || navigating || currentPage <= 1}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <ChevronLeft size={18} />
          </button>

          <span className="text-sm font-medium min-w-20 text-center">
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={loading || navigating || currentPage >= totalPages}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="text-xs text-gray-400">
          {select({ es: '© SoArtistic Protegido', en: '© SoArtistic Protected' })}
        </div>
      </div>
    </div>
  );
};

export default EbookViewer;
